/**
 * EditorPreview
 *
 * Iframe-based preview for V4 sites in the editor.
 * Uses srcdoc for instant rendering without network requests.
 * Applies viewport width constraints for responsive preview.
 *
 * Phase 2: Injects editor overlay script that enables:
 * - Hover highlights on sections
 * - Click-to-select sections
 * - Live DOM updates via postMessage
 */

import { useRef, useEffect, useState, useCallback } from 'react';
import { getEditorPreview } from '../../api/editor';
import type { EditorViewport } from '../types';
import { VIEWPORT_WIDTHS } from '../types';

// =============================================================================
// TYPES
// =============================================================================

interface EditorPreviewProps {
  websiteId: string;
  pageSlug: string;
  viewport: EditorViewport;
  zoomLevel: number;
  /** Currently selected section ID (highlights in iframe) */
  selectedSectionId?: string | null;
  /** Increment to force iframe reload (e.g., after structural changes) */
  refreshKey?: number;
  onReady?: () => void;
  onError?: (error: string) => void;
  onNavigate?: (slug: string) => void;
  /** Called when a section is clicked in the preview */
  onSectionClick?: (sectionId: string, sectionType: string) => void;
  /** Called when a specific element is clicked in the preview */
  onElementClick?: (info: ElementClickInfo) => void;
  /** The add-photo tile on a data-model gallery was clicked (model section id). */
  onGalleryAdd?: (modelSectionId: string) => void;
  /** A tile was dragged to a new position on the grid. */
  onGalleryReorder?: (modelSectionId: string, from: number, to: number) => void;
  /** A tile's two-step remove was confirmed. */
  onGalleryRemove?: (modelSectionId: string, index: number) => void;
  /**
   * Whether the injected canvas gallery UI (add tile, hover kit, drag) is
   * drawn. The portal passes the customer's gallery_editing flag — without
   * this, a flag-off customer would see tiles whose clicks go nowhere.
   */
  canvasGalleryEditing?: boolean;
  /** Optional: custom preview fetcher (for portal/customer-scoped API) */
  fetchPreview?: (websiteId: string, pageSlug: string) => Promise<string>;
}

/** Info sent when a user clicks a specific element in the preview */
export interface ElementClickInfo {
  sectionId: string;
  sectionType: string;
  /** What kind of element: 'heading', 'text', 'image', 'button' */
  elementKind: string;
  /** Index of this element among same-kind siblings within the section */
  elementIndex: number;
  /**
   * The element's own id, when the renderer stamped one as `data-el`.
   *
   * Present for sections with NAMED fields, where counting siblings is
   * meaningless: the model's order and the DOM's order are unrelated. Prefer
   * this over `elementIndex` whenever it is set.
   */
  elementId?: string | null;
  /** Current text content or image src */
  currentValue: string;
  /** For images: the existing alt text, used to seed AI generation. */
  currentAlt?: string;
}

// =============================================================================
// EDITOR INJECT SCRIPT
// =============================================================================

/**
 * Script injected into the preview iframe.
 * Handles: nav interception, section hover/click, element updates, form blocking.
 */
const EDITOR_INJECT_SCRIPT = `
<style>
  /* Editor overlay styles */
  .niftee-hover-kit { opacity: 0; transition: opacity 0.15s ease; pointer-events: none; }
  .gallery-item:hover .niftee-hover-kit { opacity: 1; }
  .niftee-hover-kit .niftee-remove-btn { pointer-events: auto; }
  .niftee-drag-over { outline: 3px solid rgba(59,130,246,0.8); outline-offset: -3px; }
  .niftee-dragging { opacity: 0.4; }
  [data-section] {
    cursor: pointer;
    transition: outline 0.15s ease, outline-offset 0.15s ease;
    outline: 2px solid transparent;
    outline-offset: -2px;
  }
  [data-section]:hover {
    outline: 2px dashed rgba(59, 130, 246, 0.5);
    outline-offset: -2px;
  }
  [data-section].niftee-selected {
    outline: 2px solid rgba(59, 130, 246, 0.8);
    outline-offset: -2px;
  }
  /* Subtle edit cursor on editable content */
  [data-section] h1, [data-section] h2, [data-section] h3,
  [data-section] h4, [data-section] h5, [data-section] h6,
  [data-section] p, [data-section] img,
  [data-section] a[class*="btn"], [data-section] button {
    cursor: pointer;
  }
  /* Hover effect on individual editable elements */
  [data-section] h1:hover, [data-section] h2:hover, [data-section] h3:hover,
  [data-section] h4:hover, [data-section] h5:hover, [data-section] h6:hover,
  [data-section] p:hover, [data-section] img:hover,
  [data-section] a[class*="btn"]:hover, [data-section] button:hover {
    outline: 1px dashed rgba(59, 130, 246, 0.4);
    outline-offset: 2px;
    border-radius: 2px;
  }
</style>
<script>
(function() {
  var selectedId = null;

  // ── FAQ FORCE-OPEN (expand all accordion/toggle items in editor mode) ──
  (function expandFAQs() {
    // Common patterns: <details> elements, hidden siblings of toggle buttons,
    // elements with max-height:0 or display:none inside FAQ sections
    document.querySelectorAll('[data-section="faq"] details').forEach(function(d) {
      d.setAttribute('open', '');
    });
    // For JS-toggled accordions: find hidden answer containers and force-show them
    document.querySelectorAll('[data-section="faq"] [style*="display: none"], [data-section="faq"] [style*="display:none"]').forEach(function(el) {
      el.style.display = '';
    });
    document.querySelectorAll('[data-section="faq"] [style*="max-height: 0"], [data-section="faq"] [style*="max-height:0"]').forEach(function(el) {
      el.style.maxHeight = 'none';
    });
    // Also expand any element with a "hidden" class inside FAQ sections
    document.querySelectorAll('[data-section="faq"] .hidden').forEach(function(el) {
      el.classList.remove('hidden');
    });
    // Disable toggle click handlers by cloning FAQ buttons (removes event listeners)
    document.querySelectorAll('[data-section="faq"] button, [data-section="faq"] [role="button"]').forEach(function(btn) {
      var clone = btn.cloneNode(true);
      if (btn.parentNode) btn.parentNode.replaceChild(clone, btn);
    });
  })();

  // ── ELEMENT KIND DETECTION ──────────────────────────────────────────

  // Leaf divs carrying text, in document order.
  //
  // Mirrors extractStatDivs in the orchestrator's parser, which is what gives
  // stats sections their editable elements. The two orderings MUST agree: the
  // index the panel sends is the parser's, and this is what resolves it back to
  // a node. A mismatch does not fail loudly — it edits the wrong figure.
  // Eyebrow badges: an element whose children are all <svg> plus a text node,
  // styled as a pill/eyebrow. Mirrors extractBadges in the orchestrator's
  // parser; the class pair is the discriminator there for the same reason it
  // is here — shape alone also matches empty decorative icon circles.
  function badgeEls(section) {
    return Array.prototype.filter.call(section.querySelectorAll('div, span'), function (el) {
      if (el.hasAttribute('data-static')) return false;
      var c = typeof el.className === 'string' ? el.className : '';
      if (c.indexOf('uppercase') === -1 || c.indexOf('tracking-') === -1) return false;
      for (var i = 0; i < el.children.length; i++) {
        if (el.children[i].tagName.toLowerCase() !== 'svg') return false;
      }
      // Mirrors extractBadges exactly. An icon-less DIV is already a leaf text
      // div to the parser, so counting it here as well would put this list out
      // of step with the model and resolve badge clicks to the wrong index.
      if (!el.children.length && el.tagName.toLowerCase() !== 'span') return false;
      return el.textContent.trim().length > 0;
    });
  }

  /**
   * The <p> elements the PARSER turns into _text_ elements.
   *
   * extractParagraphs matches /<p\s[^>]*>/ — a <p> carrying at least one
   * attribute — and skips anything under 10 characters. Counting every <p> here
   * instead put this list out of step with the model: Box Of Hope's event card
   * labels it "Date", "Location" and "Time" in short <p> tags, so the preview
   * saw 12 text nodes where the parser had 9, and every index past the first
   * label addressed a different element.
   *
   * The result was silent and confusing rather than broken: typing into DATE
   * wrote to LOCATION, LOCATION wrote into the About paragraph, and TIME landed
   * on the subtitle. Three edits, three different wrong slots.
   *
   * Keep this in step with extractParagraphs in html-parser.ts.
   */
  function editableParagraphs(section) {
    return Array.prototype.filter.call(section.querySelectorAll('p'), function (el) {
      if (!el.attributes || el.attributes.length === 0) return false;
      if (el.hasAttribute('data-static')) return false;
      return el.textContent.trim().length >= 10;
    });
  }

  function leafTextDivs(section) {
    return Array.prototype.filter.call(section.querySelectorAll('div'), function (d) {
      // data-static marks layout furniture — a renderer own "Date" caption,
      // for instance. The parser skips these, so counting them here would put
      // the two out of step and resolve clicks to the wrong element.
      if (d.hasAttribute('data-static')) return false;
      return d.children.length === 0 && d.textContent.trim().length > 0;
    });
  }

  function getElementKind(el) {
    var tag = el.tagName.toLowerCase();
    // Layout furniture is not editable, and must not be counted.
    if (el.hasAttribute && el.hasAttribute('data-static')) return null;

    // A stamped node is editable by definition — its id says exactly which
    // element it is, so the shape heuristics below are irrelevant. Without
    // this, a multi-line value (Location rendered with <br> line breaks)
    // failed every heuristic: not a leaf div, not a paragraph, kind null,
    // click dead — the one field a customer could not open.
    var stampedHost = el.closest ? el.closest('[data-el]') : null;
    if (stampedHost && !stampedHost.hasAttribute('data-static')) {
      var st = stampedHost.tagName.toLowerCase();
      if (st === 'img') return 'image';
      if (/^h[1-6]$/.test(st)) return 'heading';
      return 'text';
    }

    if (tag === 'img') return 'image';
    if (/^h[1-6]$/.test(tag)) return 'heading';

    // Badges are tested BEFORE the generic text tags. A <span> badge used to
    // fall into the 'text' branch below and then fail to resolve, because the
    // text lookup searches <p>, leaf divs and <li> — never a span. The click
    // was refused and the pill looked simply uneditable.
    if (tag === 'div' || tag === 'span') {
      var badgeCls0 = typeof el.className === 'string' ? el.className : '';
      if (badgeCls0.indexOf('uppercase') !== -1 && badgeCls0.indexOf('tracking-') !== -1 &&
          el.textContent.trim()) {
        var allSvg = true;
        for (var bj = 0; bj < el.children.length; bj++) {
          if (el.children[bj].tagName.toLowerCase() !== 'svg') { allSvg = false; break; }
        }
        if (allSvg && (el.children.length > 0 || tag === 'span')) return 'badge';
      }
    }

    if (tag === 'p' || tag === 'span' || tag === 'li') return 'text';
    // Any text-bearing leaf div, matching extractTextDivs. No longer scoped to
    // stats: the parser now treats these as editable everywhere, so restricting
    // clicks to one section type would leave the panel showing fields the
    // canvas refuses to open.
    if (tag === 'div' && el.children.length === 0 && el.textContent.trim()) {
      return 'text';
    }
    if (tag === 'button') return 'button';
    if (tag === 'a') {
      // Check if it looks like a CTA button
      var cls = el.className || '';
      if (cls.indexOf('btn') !== -1 || cls.indexOf('button') !== -1 ||
          cls.indexOf('cta') !== -1 || cls.indexOf('rounded') !== -1) {
        return 'button';
      }
      return null; // Regular nav link, don't treat as editable
    }
    return null;
  }

  function indexOfIn(list, el) {
    for (var i = 0; i < list.length; i++) {
      if (list[i] === el || list[i].contains(el)) return i;
    }
    return 0;
  }

  function getElementIndex(el, kind, section) {
    var selector;
    if (kind === 'badge') return indexOfIn(badgeEls(section), el);
    if (kind === 'heading') selector = 'h1, h2, h3, h4, h5, h6';
    else if (kind === 'text') selector = 'p';
    else if (kind === 'image') selector = 'img';
    else if (kind === 'button') selector = 'a[class*="btn"], a[class*="button"], a[class*="cta"], button, a[class*="rounded"]';
    else return 0;
    var siblings = section.querySelectorAll(selector);
    // Paragraphs, then leaf divs, then list items — the exact order
    // extractElements produces, since each extractor continues the same _text_
    // index. A section holding more than one kind would otherwise resolve to
    // the wrong node, or to none.
    if (kind === 'text') {
      siblings = editableParagraphs(section)
        .concat(leafTextDivs(section))
        .concat(Array.prototype.slice.call(section.querySelectorAll('li')));
    }
    for (var i = 0; i < siblings.length; i++) {
      if (siblings[i] === el || siblings[i].contains(el)) return i;
    }
    // NOT 0.
    //
    // Returning 0 here meant an element the lookup could not place silently
    // resolved to the FIRST element of its kind in the section. Clicking a
    // checklist <li> — which no selector matched — therefore edited the card's
    // description paragraph, overwriting it. -1 makes the caller refuse.
    return -1;
  }

  function getElementValue(el, kind) {
    if (kind === 'image') return el.getAttribute('src') || '';
    if (kind === 'button') {
      // Strip SVG icons — return only the visible text content
      var clone = el.cloneNode(true);
      var svgs = clone.querySelectorAll('svg');
      for (var i = 0; i < svgs.length; i++) svgs[i].remove();
      return (clone.textContent || '').replace(/\\s+/g, ' ').trim();
    }
    // Strip icons, exactly as the button branch above does.
    //
    // This returned innerHTML, so clicking anything containing an <svg> — a
    // checklist item, an icon-led line — captured the icon's markup as the
    // element's "value". That markup was then written into the content model
    // and patched into the page as text.
    var textClone = el.cloneNode(true);
    var textSvgs = textClone.querySelectorAll('svg');
    for (var s = 0; s < textSvgs.length; s++) textSvgs[s].remove();
    return (textClone.innerHTML || textClone.textContent || '').trim();
  }

  // ── NAV INTERCEPTION ──────────────────────────────────────────────
  document.addEventListener('click', function(e) {
    // A click that ends a drag is not an edit.
    if (justDragged) {
      e.preventDefault();
      e.stopPropagation();
      justDragged = false;
      return;
    }

    // Two-step remove: first click arms, second confirms, any other click
    // disarms. Undo covers mistakes, so no dialog.
    var removeBtn = e.target.closest ? e.target.closest('.niftee-remove-btn') : null;
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (removeBtn.hasAttribute('data-armed')) {
        var rTile = removeBtn.closest('.gallery-item');
        window.parent.postMessage({
          type: 'niftee-gallery-remove',
          modelSectionId: modelSectionOf(removeBtn),
          index: rTile ? Number(rTile.getAttribute('data-index')) : -1
        }, '*');
        disarmRemove();
      } else {
        disarmRemove();
        removeBtn.setAttribute('data-armed', '');
        removeBtn.textContent = 'Remove?';
        removeBtn.style.width = 'auto';
        removeBtn.style.padding = '0 8px';
        armedRemoveBtn = removeBtn;
      }
      return;
    }
    disarmRemove();

    // The add tile is editor furniture, not content — it must never fall
    // through to element resolution.
    var addTile = e.target.closest ? e.target.closest('.niftee-add-tile') : null;
    if (addTile) {
      e.preventDefault();
      e.stopPropagation();
      window.parent.postMessage({
        type: 'niftee-gallery-add',
        modelSectionId: addTile.getAttribute('data-model-section')
      }, '*');
      return;
    }

    // Check for section click first
    var section = e.target.closest('[data-section]');
    if (section) {
      e.preventDefault();
      e.stopPropagation();
      var sectionId = section.getAttribute('data-section-id');
      var sectionType = section.getAttribute('data-section');

      // Try to detect a specific element click
      var target = e.target;
      var editableEl = null;
      var kind = null;
      // Walk up from click target to find nearest editable element (but stay within section)
      var current = target;
      while (current && current !== section) {
        kind = getElementKind(current);
        if (kind) { editableEl = current; break; }
        current = current.parentElement;
      }
      // Also check if the target itself is editable
      if (!editableEl) {
        kind = getElementKind(target);
        if (kind) editableEl = target;
      }

      if (sectionId && sectionType) {
        // Deselect previous
        if (selectedId) {
          var prev = document.querySelector('[data-section-id="' + selectedId + '"]');
          if (prev) prev.classList.remove('niftee-selected');
        }
        // Select new
        section.classList.add('niftee-selected');
        selectedId = sectionId;

        // If we identified a specific element, send element click
        if (editableEl && kind) {
          // A renderer that knows its own element ids stamps them as data-el.
          // Prefer that over counting: for a section with NAMED fields the
          // model's order has no relationship to DOM order, so the nth-of-kind
          // lookup below scrambles them — an eyebrow edit landing on the
          // tagline and the alt text at once.
          var stamped = editableEl.closest ? editableEl.closest('[data-el]') : null;
          var stampedId = stamped ? stamped.getAttribute('data-el') : null;

          var idx = stampedId ? 0 : getElementIndex(editableEl, kind, section);
          // The lookup could not place this element. Selecting the section is
          // still useful, but sending an index we know is wrong is how a click
          // on one element ends up editing another.
          if (!stampedId && idx < 0) return;
          window.parent.postMessage({
            type: 'niftee-editor-element-click',
            sectionId: sectionId,
            sectionType: sectionType,
            elementKind: kind,
            elementIndex: idx,
            elementId: stampedId,
            currentValue: getElementValue(stamped || editableEl, kind),
            // The image's own alt text. It is the most reliable description of
            // what the slot should show, so it seeds the AI generate form.
            currentAlt: kind === 'image' ? (editableEl.getAttribute('alt') || '') : ''
          }, '*');
        } else {
          // Fallback: section-level click (opens side panel)
          window.parent.postMessage({
            type: 'niftee-editor-section-click',
            sectionId: sectionId,
            sectionType: sectionType
          }, '*');
        }
      }
      return;
    }

    // Nav link interception
    var link = e.target.closest('a');
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href) return;
    if (href.startsWith('http') && !href.includes(window.location.host)) {
      link.setAttribute('target', '_blank');
      return;
    }
    e.preventDefault();
    var slug = href;
    if (href === '/' || href === '/index.html' || href === 'index.html') {
      slug = 'home';
    } else {
      slug = href.replace(/^\\//, '').replace(/\\.html$/, '').replace(/\\/$/, '');
      if (slug === 'index' || slug === '') slug = 'home';
    }
    window.parent.postMessage({ type: 'niftee-editor-navigate', slug: slug }, '*');
  }, true);

  // ── FORM BLOCKING ─────────────────────────────────────────────────
  document.addEventListener('submit', function(e) { e.preventDefault(); });

  // ── PARENT MESSAGE LISTENER ───────────────────────────────────────
  window.addEventListener('message', function(e) {
    var msg = e.data;
    if (!msg || !msg.type) return;

    // Highlight a section
    if (msg.type === 'niftee-highlight-section') {
      if (selectedId) {
        var prev = document.querySelector('[data-section-id="' + selectedId + '"]');
        if (prev) prev.classList.remove('niftee-selected');
      }
      if (msg.sectionId) {
        var el = document.querySelector('[data-section-id="' + msg.sectionId + '"]');
        if (el) {
          el.classList.add('niftee-selected');
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        selectedId = msg.sectionId;
      } else {
        selectedId = null;
      }
    }

    // Deselect all
    if (msg.type === 'niftee-deselect') {
      if (selectedId) {
        var prev = document.querySelector('[data-section-id="' + selectedId + '"]');
        if (prev) prev.classList.remove('niftee-selected');
        selectedId = null;
      }
    }

    // Update a text element's content
    if (msg.type === 'niftee-update-text') {
      var section = document.querySelector('[data-section-id="' + msg.sectionId + '"]');
      if (!section) return;
      // Prefer the stamped node, but FALL THROUGH when there is none.
      //
      // Every edit carries an elementId now, while only renderers that know
      // their own ids stamp data-el. Returning early on a miss would send every
      // frozen-section and gallery edit down the reload path instead of
      // updating live — slower everywhere, for the sake of one section type.
      // Alt text has no visible node; write it onto the slot's img directly
      // rather than degrading to a reload.
      if (msg.elementId && /_images_\\d+_alt$/.test(msg.elementId)) {
        var altImg = section.querySelector('[data-el="' + msg.elementId.replace(/_alt$/, '_url') + '"]');
        if (altImg) { altImg.setAttribute('alt', msg.newValue); return; }
      }

      var stampedEl = msg.elementId
        ? section.querySelector('[data-el="' + msg.elementId + '"]')
        : null;
      if (stampedEl) {
        var keep = stampedEl.querySelectorAll ? stampedEl.querySelectorAll('svg') : [];
        var keepHtml = '';
        for (var k = 0; k < keep.length; k++) keepHtml += keep[k].outerHTML + ' ';
        stampedEl.innerHTML = keepHtml + msg.newValue;
        return;
      }
      // A section that stamps ANY of its nodes is a named section, and counting
      // inside one is meaningless — the field is simply not rendered yet (an
      // empty tagline emits no node). Falling through here painted tagline
      // keystrokes into the DATE cell, live, until a reload put them right.
      // Report the miss so the preview re-fetches instead.
      if (msg.elementId && section.querySelector('[data-el]')) {
        window.parent.postMessage({ type: 'niftee-update-missed', sectionId: msg.sectionId }, '*');
        return;
      }
      // Find the element by its index and type within the section
      var els;
      if (msg.elementType === 'heading') {
        els = section.querySelectorAll('h1, h2, h3, h4, h5, h6');
      } else if (msg.elementType === 'text') {
        // Paragraphs then leaf divs, mirroring the model's _text_ ordering.
        // Before this the lookup was <p> alone, so a section built from divs
        // resolved to nothing: the edit saved, the markup was patched, and the
        // preview only caught up on a full page reload.
        els = editableParagraphs(section)
          .concat(leafTextDivs(section))
          .concat(Array.prototype.slice.call(section.querySelectorAll('li')));
      } else if (msg.elementType === 'badge') {
        els = badgeEls(section);
        if (els && els[msg.elementIndex] !== undefined) {
          var badge = els[msg.elementIndex];
          // Rewrite only the label. Setting innerHTML wholesale would delete
          // the icon that makes it a badge.
          var badgeSvg = badge.querySelector('svg');
          badge.innerHTML = (badgeSvg ? badgeSvg.outerHTML + ' ' : '') + msg.newValue;
        }
        els = null;
      } else if (msg.elementType === 'cta' || msg.elementType === 'button') {
        els = section.querySelectorAll('.btn-primary, .btn-secondary, [class*="btn"]');
        if (els && els[msg.elementIndex] !== undefined) {
          var btn = els[msg.elementIndex];
          // Preserve existing SVG icon if present
          var existingSvg = '';
          var svgEl = btn.querySelector('svg');
          if (svgEl) existingSvg = svgEl.outerHTML + ' ';
          btn.innerHTML = existingSvg + msg.newValue;
        }
        els = null; // skip the generic handler below
      }
      if (els && els[msg.elementIndex] !== undefined) {
        // Keep any icon the element carries.
        //
        // Setting innerHTML wholesale replaces everything inside, so editing a
        // checklist bullet visibly deleted its checkmark. The server preserves
        // it — patchTextContent rewrites only the text between the icon and the
        // closing tag — so the markup was always intact and a reload restored
        // it. The preview was simply lying about what had been saved, which is
        // worse than it sounds: it invites someone to "fix" damage that was
        // never done.
        //
        // The button and badge branches above already do this.
        var textNode = els[msg.elementIndex];
        var keptIcons = textNode.querySelectorAll ? textNode.querySelectorAll('svg') : [];
        var iconHtml = '';
        for (var ic = 0; ic < keptIcons.length; ic++) iconHtml += keptIcons[ic].outerHTML + ' ';
        els[msg.elementIndex].innerHTML = iconHtml + msg.newValue;
      } else if (els) {
        // No such node. An optional field that is currently empty renders
        // nothing, so there is nowhere to write — which looked exactly like a
        // save that did not happen. Ask the parent for a reload instead of
        // silently doing nothing.
        window.parent.postMessage({ type: 'niftee-update-missed', sectionId: msg.sectionId }, '*');
      }
    }

    // Update an image element's src
    if (msg.type === 'niftee-update-image') {
      var section = document.querySelector('[data-section-id="' + msg.sectionId + '"]');
      if (!section) return;
      // Same fall-through rule as the text path above.
      var stampedImg = msg.elementId
        ? section.querySelector('img[data-el="' + msg.elementId + '"]')
        : null;
      // Same rule as text: a stamped section never falls back to counting.
      if (!stampedImg && msg.elementId && section.querySelector('[data-el]')) {
        window.parent.postMessage({ type: 'niftee-update-missed', sectionId: msg.sectionId }, '*');
        return;
      }
      if (stampedImg) {
        stampedImg.src = msg.newValue;
        stampedImg.classList.remove('hidden', 'opacity-0');
        stampedImg.removeAttribute('data-placeholder');
        var ov = stampedImg.parentElement && stampedImg.parentElement.querySelector('[data-placeholder-overlay]');
        if (ov) ov.style.display = 'none';
        return;
      }
      var imgs = section.querySelectorAll('img');
      if (imgs[msg.elementIndex] === undefined) {
        window.parent.postMessage({ type: 'niftee-update-missed', sectionId: msg.sectionId }, '*');
      }
      if (imgs[msg.elementIndex] !== undefined) {
        var img = imgs[msg.elementIndex];
        img.src = msg.newValue;
        // If this was a placeholder image, show it and hide the placeholder text
        if (img.classList.contains('hidden') || img.getAttribute('data-placeholder')) {
          // 'opacity-0' belongs to the gallery placeholder, where the <img>
          // covers the tile invisibly so that a CLICK lands on an image rather
          // than on the prompt text beneath it. Leaving it on would fill the
          // slot with a picture nobody can see.
          img.classList.remove('hidden', 'opacity-0');
          img.removeAttribute('data-placeholder');
          var overlay = img.parentElement && img.parentElement.querySelector('[data-placeholder-overlay]');
          if (overlay) overlay.style.display = 'none';
          // Older placeholders put the prompt in a sibling span and the dashed
          // border on the <img>'s own parent. Still handled: other section
          // types emit that shape.
          var sibling = img.nextElementSibling || img.parentElement.querySelector('span');
          if (sibling && sibling.tagName === 'SPAN') sibling.style.display = 'none';
          var container = img.parentElement;
          if (container) {
            container.classList.remove('border-2', 'border-dashed', 'border-gray-300', 'bg-gray-100');
            container.classList.remove('flex', 'items-center', 'justify-center');
          }
        }
      }
    }
  });

  // ── ADD SECTION IDS ───────────────────────────────────────────────
  // V4 generation adds data-section but we also need data-section-id
  // for unique identification. Generate IDs from page context.
  (function addSectionIds() {
    var pageSlug = document.title.trim().toLowerCase();
    // Index by ABSOLUTE position, matching the content model.
    //
    // This counted per type — the second 'about' section became 'about_1'.
    // The content model ids are '{slug}_{type}_{absoluteIndex}', so a page
    // with two about sections produced 'about_1' from the iframe and
    // 'index_about_1' from the model, and the lookup
    // (id.endsWith with the section id) matched them — two different sections
    // that happened to share a suffix.
    //
    // Clicking an image in the second about section therefore resolved to the
    // first one, which had no image elements, and the save failed with
    // "Couldn't match this image to save it". Only the hero was unaffected,
    // because it is both first-of-type and absolute index 0.
    var sections = document.querySelectorAll('[data-section]');
    sections.forEach(function(section, i) {
      var type = section.getAttribute('data-section');
      section.setAttribute('data-section-id', type + '_' + i);
    });
  })();

  // ---------------------------------------------------------------------
  // Editor-only furniture, drawn here so it can NEVER ship to a live page.
  // Stored markup keeps only an invisible img[data-placeholder] per empty
  // slot; the visible dashed prompt and the add-photo tile exist solely in
  // this preview.
  // ---------------------------------------------------------------------
  function drawPlaceholderChrome() {
    var imgs = document.querySelectorAll('img[data-placeholder]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      var parent = img.parentElement;
      if (!parent || parent.querySelector('[data-placeholder-overlay]')) continue;
      var ov = document.createElement('div');
      ov.setAttribute('data-placeholder-overlay', '');
      ov.style.cssText = 'position:absolute;inset:0;border:2px dashed rgba(120,120,120,0.45);border-radius:8px;display:flex;align-items:center;justify-content:center;pointer-events:none;';
      var label = document.createElement('span');
      label.textContent = 'Click to add image';
      label.style.cssText = 'font-size:13px;color:rgba(120,120,120,0.9);pointer-events:none;';
      ov.appendChild(label);
      if (getComputedStyle(parent).position === 'static') parent.style.position = 'relative';
      parent.insertBefore(ov, img);
    }
  }

  function drawGalleryAddTiles() {
    var grids = document.querySelectorAll('[data-section="gallery"] [id$="-grid"]');
    for (var g = 0; g < grids.length; g++) {
      var grid = grids[g];
      if (grid.querySelector('.niftee-add-tile')) continue;
      var tile = document.createElement('div');
      tile.className = 'niftee-add-tile';
      // The grid id IS the model section id plus '-grid' — the one identifier
      // that survives reorders, unlike the positional data-section-id.
      tile.setAttribute('data-model-section', grid.id.replace(/-grid$/, ''));
      tile.style.cssText = 'aspect-ratio:1/1;border:2px dashed rgba(59,130,246,0.55);border-radius:12px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;cursor:pointer;color:rgba(59,130,246,0.9);background:rgba(59,130,246,0.05);';
      tile.innerHTML = '<div style="font-size:26px;line-height:1;">+</div><div style="font-size:13px;font-weight:600;">Add photo</div>';
      grid.appendChild(tile);
    }
  }

  // Hover kit per filled gallery tile: center grip (affordance only - the
  // WHOLE tile drags, so there is no dead zone and no tiny drag target),
  // bottom pill, top-right remove with a two-step inline confirm. Drawn here
  // so none of it can ship to a live page.
  var armedRemoveBtn = null;
  function disarmRemove() {
    if (!armedRemoveBtn) return;
    armedRemoveBtn.textContent = String.fromCharCode(215);
    armedRemoveBtn.style.width = '24px';
    armedRemoveBtn.removeAttribute('data-armed');
    armedRemoveBtn = null;
  }

  function drawGalleryHoverKit() {
    var grids = document.querySelectorAll('[data-section="gallery"] [id$="-grid"]');
    for (var g = 0; g < grids.length; g++) {
      var grid = grids[g];
      var tiles = grid.querySelectorAll('.gallery-item');
      var filled = [];
      for (var t = 0; t < tiles.length; t++) {
        if (!tiles[t].querySelector('img[data-placeholder]')) filled.push(tiles[t]);
      }
      for (var k = 0; k < filled.length; k++) {
        var tile = filled[k];
        tile.setAttribute('draggable', 'true');
        if (tile.querySelector('.niftee-hover-kit')) continue;

        var kit = document.createElement('div');
        kit.className = 'niftee-hover-kit';
        kit.style.cssText = 'position:absolute;inset:0;';

        var grip = document.createElement('div');
        grip.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:34px;height:34px;border-radius:8px;background:rgba(0,0,0,0.55);color:#fff;display:flex;align-items:center;justify-content:center;font-size:15px;letter-spacing:2px;';
        grip.textContent = '\u22ee\u22ee';
        kit.appendChild(grip);

        var pill = document.createElement('div');
        pill.style.cssText = 'position:absolute;left:50%;bottom:8px;transform:translateX(-50%);background:rgba(0,0,0,0.65);color:#fff;font-size:11px;padding:3px 10px;border-radius:999px;white-space:nowrap;';
        pill.textContent = 'Click to edit \u00b7 drag to reorder';
        kit.appendChild(pill);

        // Panel parity: a gallery keeps at least one photo, so no remove
        // control when only one remains.
        if (filled.length > 1) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'niftee-remove-btn';
          btn.textContent = String.fromCharCode(215);
          btn.style.cssText = 'position:absolute;top:6px;right:6px;height:24px;width:24px;border:none;border-radius:6px;background:rgba(220,38,38,0.9);color:#fff;font-size:14px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;';
          kit.appendChild(btn);
        }
        tile.appendChild(kit);
      }
    }
  }

  function modelSectionOf(el) {
    var grid = el.closest ? el.closest('[id$="-grid"]') : null;
    return grid ? grid.id.replace(/-grid$/, '') : null;
  }

  var dragFrom = null;
  var dragSection = null;
  var justDragged = false;

  document.addEventListener('dragstart', function(e) {
    if (!galleryCanvasOn) return;
    var tile = e.target.closest ? e.target.closest('.gallery-item') : null;
    if (!tile || !tile.hasAttribute('data-index')) return;
    dragFrom = Number(tile.getAttribute('data-index'));
    dragSection = modelSectionOf(tile);
    tile.classList.add('niftee-dragging');
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', String(dragFrom)); } catch (err) {}
    }
  });

  document.addEventListener('dragover', function(e) {
    var tile = e.target.closest ? e.target.closest('.gallery-item') : null;
    if (!tile || dragSection === null || modelSectionOf(tile) !== dragSection) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    tile.classList.add('niftee-drag-over');
  });

  document.addEventListener('dragleave', function(e) {
    var tile = e.target.closest ? e.target.closest('.gallery-item') : null;
    if (tile) tile.classList.remove('niftee-drag-over');
  });

  document.addEventListener('drop', function(e) {
    var tile = e.target.closest ? e.target.closest('.gallery-item') : null;
    if (!tile || dragSection === null || modelSectionOf(tile) !== dragSection) return;
    e.preventDefault();
    tile.classList.remove('niftee-drag-over');
    var to = Number(tile.getAttribute('data-index'));
    if (to !== dragFrom) {
      justDragged = true;
      window.parent.postMessage({
        type: 'niftee-gallery-reorder',
        modelSectionId: dragSection,
        from: dragFrom,
        to: to
      }, '*');
    }
  });

  document.addEventListener('dragend', function() {
    dragFrom = null;
    dragSection = null;
    var marked = document.querySelectorAll('.niftee-dragging, .niftee-drag-over');
    for (var i = 0; i < marked.length; i++) marked[i].classList.remove('niftee-dragging', 'niftee-drag-over');
    // A click can fire right after a drop; swallow exactly one.
    setTimeout(function() { justDragged = false; }, 250);
  });

  var editorCfg = window.__nifteeEditorCfg || {};
  var galleryCanvasOn = editorCfg.galleryCanvas !== false;

  drawPlaceholderChrome();
  if (galleryCanvasOn) {
    drawGalleryAddTiles();
    drawGalleryHoverKit();
  }

  // Notify parent that iframe is ready
  window.parent.postMessage({ type: 'niftee-editor-ready' }, '*');
})();
</script>
`;

// =============================================================================
// COMPONENT
// =============================================================================

export function EditorPreview({
  websiteId,
  pageSlug,
  viewport,
  zoomLevel,
  selectedSectionId,
  refreshKey,
  onReady,
  onError,
  onNavigate,
  onSectionClick,
  onElementClick,
  onGalleryAdd,
  onGalleryReorder,
  onGalleryRemove,
  canvasGalleryEditing = true,
  fetchPreview,
}: EditorPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const msg = event.data;
      if (!msg?.type) return;

      if (msg.type === 'niftee-editor-navigate') {
        onNavigate?.(msg.slug);
      }
      if (msg.type === 'niftee-editor-section-click') {
        onSectionClick?.(msg.sectionId, msg.sectionType);
      }
      if (msg.type === 'niftee-editor-element-click') {
        onElementClick?.({
          sectionId: msg.sectionId,
          sectionType: msg.sectionType,
          elementKind: msg.elementKind,
          elementIndex: msg.elementIndex,
          elementId: msg.elementId,
          currentValue: msg.currentValue,
          currentAlt: msg.currentAlt,
        });
      }
      if (msg.type === 'niftee-editor-ready') {
        onReady?.();
      }
      if (msg.type === 'niftee-gallery-add' && msg.modelSectionId) {
        onGalleryAdd?.(String(msg.modelSectionId));
      }
      if (msg.type === 'niftee-gallery-reorder' && msg.modelSectionId) {
        onGalleryReorder?.(String(msg.modelSectionId), Number(msg.from), Number(msg.to));
      }
      if (msg.type === 'niftee-gallery-remove' && msg.modelSectionId && msg.index >= 0) {
        onGalleryRemove?.(String(msg.modelSectionId), Number(msg.index));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onNavigate, onSectionClick, onElementClick, onReady, onGalleryAdd, onGalleryReorder, onGalleryRemove]);

  // Highlight selected section in iframe when selection changes
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    iframe.contentWindow.postMessage(
      {
        type: selectedSectionId ? 'niftee-highlight-section' : 'niftee-deselect',
        sectionId: selectedSectionId || null,
      },
      '*'
    );
  }, [selectedSectionId]);

  // Load page HTML
  const loadPage = useCallback(async () => {
    if (!websiteId || !pageSlug) return;

    setIsLoading(true);
    try {
      const fetcher = fetchPreview || getEditorPreview;
      let html = await fetcher(websiteId, pageSlug);

      // Config rides in ahead of the editor script — the script draws its
      // gallery furniture at load, so a postMessage would race it.
      const configScript = `<script>window.__nifteeEditorCfg=${JSON.stringify({ galleryCanvas: canvasGalleryEditing })};</` + `script>`;
      const inject = configScript + EDITOR_INJECT_SCRIPT;

      // Inject editor script before </body>
      if (html.includes('</body>')) {
        html = html.replace('</body>', `${inject}</body>`);
      } else {
        html = html + inject;
      }

      setHtmlContent(html);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load preview';
      onError?.(message);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId, pageSlug, refreshKey, onError, fetchPreview, canvasGalleryEditing]);
  // Reload when the canvas had nowhere to write a live update.
  //
  // An optional field that is currently empty renders nothing — an event with
  // no details paragraph emits no <p>, a gallery with no photo used to emit no
  // <img> — so the update silently did nothing and the edit looked unsaved. It
  // HAD saved; the canvas simply could not show it.
  //
  // Reloading is cheap and always correct, and it covers every optional field
  // in every renderer rather than each one having to be remembered separately.
  //
  // DEBOUNCED, and that is not a detail. Every keystroke into a field that is
  // still empty misses, so reloading on each one made the canvas flash and
  // clobbered saves that were still in flight — losing the last characters
  // typed. The reload is only useful once, after the field stops being empty,
  // so it waits for typing to stop and then runs a single time.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onMissed = (event: MessageEvent) => {
      if (event.data?.type !== 'niftee-update-missed') return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        loadPage();
      }, 1500);
    };
    window.addEventListener('message', onMissed);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('message', onMissed);
    };
  }, [loadPage]);


  // Reload when page changes or refreshKey increments
  useEffect(() => {
    loadPage();
  }, [loadPage]);

  // Note: Live DOM updates are now sent via postMessage from EditorPage
  // when element values change in the EditPanel.

  // Calculate iframe dimensions
  const viewportWidth = VIEWPORT_WIDTHS[viewport];
  const scale = zoomLevel / 100;

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    height: '100%',
    minHeight: 'calc(100vh - 56px)',
    overflow: 'auto',
    backgroundColor: '#f3f4f6',
    padding: viewport === 'desktop' ? '0' : '16px',
  };

  const wrapperStyle: React.CSSProperties = {
    width: viewport === 'desktop' ? '100%' : `${viewportWidth}px`,
    height: viewport === 'desktop' ? 'calc(100vh - 56px)' : 'calc(100vh - 88px)',
    transform: scale !== 1 ? `scale(${scale})` : undefined,
    transformOrigin: 'top center',
    transition: 'width 0.3s ease, transform 0.2s ease',
    boxShadow:
      viewport !== 'desktop'
        ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        : undefined,
    borderRadius: viewport !== 'desktop' ? '8px' : undefined,
    overflow: 'hidden',
    backgroundColor: 'white',
  };

  return (
    <div style={containerStyle}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <span className="text-sm text-gray-500">Loading preview...</span>
          </div>
        </div>
      )}

      <div style={wrapperStyle}>
        <iframe
          ref={iframeRef}
          srcDoc={htmlContent}
          title="Site Preview"
          sandbox="allow-same-origin allow-scripts"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.2s ease',
          }}
        />
      </div>
    </div>
  );
}

